import { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import { Box, Grid, IconButton, InputAdornment, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, Button } from "@mui/material";
import { CsvParameters, downloadAsCSV } from "../../../actions/Users/authenticateCsvDownload";
import DownloadCSV, {clearCsvDownload} from "../../DownloadCSV";
import Download from '../../../asset/image/DownloadUsage.svg';
import { Pagination } from "../../Pagination";
import { getDecodeURI, getEncodedURI } from '../../../utils/util';
import SortArrows,  { SortOrder } from "../../SortArrows";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from '@mui/icons-material/Close';
import { getStarlinkAuditLogs, clearComponentData } from "../slice";
import moment from "moment-timezone";
import { JsonDataTooltip } from "../../JSONTooltip/JsonTooltip";
import { MMDDYYYYHMMSS_DATE_FORMAT_24_HRS } from "../../../utils/constants";
import { convertDateTimeIntoTimezone } from "../../../utils/util";

const convertToCsv = (data: any[][]) => {
    const csvRows: any[] = [];
    data[1]?.[0].forEach((row) => {
        csvRows.push([row.IpAddress, row.accountNumber, row.createdAt, row.dpId,  row.serviceLineName, row.serviceLineNumber, row.serviceLineStatus? "Active": "Inactive", row.resource,  row.users, row.userAction, row.metaData?.response?? row.metaData?.resp ?? row.metaData?.message, row.metaData?.data ?? ""])
    })
    return [['IpAddress', 'AccountNumber', 'CreatedAt', 'DP Id', 'ServiceLine Name', 'ServiceLine Number', 'ServiceLine Status', 'Resource', 'Users', 'User Action', 'Response', "Data"], ...csvRows]
}

export type UserTerminalSortBy = 'serviceLineName' | 'users' | 'description' | 'resource' | 'userAction' | 'IpAddress' | 'createdAt' ;

function AuditLogs(props) {

    const { authReducer, errorReducer, history, location, dpIds, userTerminals, totalAuditLogs, auditLogs, getStarlinkAuditLogs, newSummaryServiceLine, clearComponentData } = props;

    const queryParams = new Map<string, string>(Object.entries(getDecodeURI(location.search)));

    const [searchText, setSearchText] = useState<string>(queryParams.get('search') || '');
    const [switchServiceLineToggle, setSwitchServiceLineToggle] = useState(false)
    const [isSLUpdated, setIsSLUpdated] = useState(false); 
    const firstRun = useRef(true);
    const prevSummaryRef = useRef<any>(null);
    const [tableParams, setTableParams] = useState<{
        page: number;
        size: number;
        sortBy: UserTerminalSortBy;
        sortOrder: SortOrder;
        search: string;
    }>({
        page: queryParams.has('page') ? parseInt(queryParams.get('page') as string) : 1,
        size: queryParams.has('size') ? parseInt(queryParams.get('size') as string) : 10,
        sortBy: queryParams.has('sortBy') ? queryParams.get('sortBy')  as UserTerminalSortBy: 'createdAt',
        sortOrder: queryParams.has('sortOrder') ? queryParams.get('sortOrder') as SortOrder : 'desc',
        search: queryParams.has('search') ? queryParams.get('search') as string : ''
    });
    const searchTimeoutRef = useRef<any>();
    const dispatch = useDispatch();

    const handleChangeSize = (event: any) => {
        setTableParams({ ...tableParams, size: event.target.value, page: 1 });
    };

    const handleChangeSorting = (sortBy: UserTerminalSortBy, sortOrder: SortOrder) => {
        setTableParams({
            ...tableParams,
            sortBy: sortBy,
            sortOrder: sortOrder,
            page: 1
        });
    }

    const handleOnSearchChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setSearchText(e.target.value);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            searchTimeoutRef.current = undefined;
            handleSearch(e.target.value);
        }, 1000)
    }

    const handleChangePage = (event: any, newPage: number) => {
        setTableParams({ ...tableParams, page: newPage });
    };

    const handleSearch = (value: string) => {
        setTableParams({
            ...tableParams,
            search: value,
            page: 1
        });
    }

    const handleOnSearchClick = () => {
        handleSearch(searchText);
    }

    const handleOnSearchClear = () => {
        setSearchText('');
        handleSearch('');
    }


    const updateQueryParams = () => {
        queryParams.set('page', tableParams.page.toString())
        queryParams.set('size', tableParams.size.toString())
        queryParams.set('sortBy', tableParams.sortBy)
        queryParams.set('sortOrder', tableParams.sortOrder)
        queryParams.set('search', tableParams.search)
        history.replace({ pathname: location.pathname, search: `?${getEncodedURI(Object.fromEntries(queryParams.entries()))}` });
    }

    useEffect(() => {
        updateQueryParams();
        
        clearComponentData();

        const controller = new AbortController();

        getStarlinkAuditLogs({
            searchText: tableParams.search,
            sortBy: tableParams.sortBy,
            sortOrder: tableParams.sortOrder,
            page: tableParams.page,
            limit: tableParams.size,
            dpIds,
           serviceLines: Array.isArray(newSummaryServiceLine) ? newSummaryServiceLine?.filter(sl=>sl !== '') : [],
            signal: controller.signal
        });

        return () => controller.abort();

    }, [tableParams, newSummaryServiceLine, dpIds]);

    
    const handleAuditLogDownload = () => {
        const params: CsvParameters = {
            type: 'QUERY_PG',
            id : "audit-log-reports",
            queryName: "GET_STARLINK_AUDIT_LOGS",
            payload: {
                dpIds: dpIds.map(dpId => `'${dpId}'`).join(','),
                searchText: tableParams.search,
                sortBy: tableParams.sortBy,
                sortOrder: tableParams.sortOrder,
                limit: totalAuditLogs,
                offset: 0,
                page: 1,
                serviceLines: Array.isArray(newSummaryServiceLine) ? newSummaryServiceLine?.filter(sl=>sl !== '').map(serviceLine => `'${serviceLine}'`).join(',') : []
            }    
        }
        dispatch(downloadAsCSV(params));
    }

    useEffect(() => {
        if (authReducer.csvDataDownloded && authReducer.csvDataDownloded.length > 0) {
            if (authReducer.csvDownloadId === 'audit-log-reports') {
                const [columns,  ...rows] = authReducer.csvDataDownloded;
                const filename = "audit-log-report";
                const csvContent = convertToCsv([columns, ...rows]);
                DownloadCSV(csvContent, {
                  formatters: {
                    0: 'ESC-COMMA',
                    4: 'ESC-COMMA',
                    5: 'ESC-COMMA',
                    6: 'ESC-COMMA',
                    7: 'ESC-COMMA',
                    8: 'ESC-COMMA',

                    10: (value) => {
                        if (Array.isArray(value)) {
                            return "";
                        }
                       else if (typeof value === "object" && value !== null) {
                            return JSON.stringify(value)
                            .replace(/,/g, ';')
                            .replace(/\n/g, ' ');
                        }
                        return String(value ?? "").replace(/,/g, ';');
                    },
                    11: (value) => {
                        if (typeof value === "object" && value !== null) {
                            return JSON.stringify(value)
                            .replace(/,/g, ';')
                            .replace(/\n/g, ' ');
                        }
                        return String(value ?? "").replace(/,/g, ';');
                    },
                }
                }, filename);
                return () => {
                    dispatch(clearCsvDownload())
                }
            }
        }
    }, [authReducer.csvDataDownloded])
 

    return (
        <Paper elevation={1} classes={{ root: 'starlink--base--mtb_07x starlink--base--padding_1x add-srcoll-starlink-subtab-pages' }}>
            {/* FILTERS */}
            <Grid classes={{ root: 'starlink--base--mtb_07x starlink--base--flex--align--center starlink--base--flex--justify--space_between' }}>
                <Grid>
                    <Typography component="div" classes={{ root: 'starlink--base--font_1x' }}>
                        AUDIT LOGS
                    </Typography>
                </Grid>
                <Grid classes={{ root: 'starlink--base--flex starlink--base--flex--align--center' }}>
                  
                    <Grid item xs={6} sm={6} md={6} lg={6}>
                        <Box>
                            <TextField
                                id="search"
                                variant="outlined"
                                placeholder="Type something"
                                autoComplete="off"
                                classes={{root: "input-box-user-ter"}}
                                size="small"
                                value={searchText}
                                onChange={handleOnSearchChange}
                                onKeyDown={(e) => { if (e.key === 'Enter') { handleOnSearchClick() } }}
                                InputLabelProps={{ className: "serachLabel" }}
                                InputProps={{
                                    className: "serachBar",
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {!searchText && <SearchIcon
                                                className="cursorPointer input-search-icons" onClick={handleOnSearchClick}
                                            />}
                                            {searchText && <CloseIcon
                                                className="cursorPointer input-search-icons"
                                                onClick={handleOnSearchClear}
                                            />}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Grid>
                    {auditLogs.length > 0 && <div className="download-summary-btn" onClick={handleAuditLogDownload}><img className="downloadImg" src={Download} alt="download" /><Button>Download</Button></div>}
                </Grid>
            </Grid>

            <Pagination count={totalAuditLogs} onPageChange={handleChangePage} page={tableParams.page} rowsPerPage={tableParams.size} onRowsPerPageChange={handleChangeSize} />

            
            <TableContainer component={Paper} className="starlink--table_container">
                <Table sx={{ minWidth: 650 }} size="small" aria-label="starlink--service_accounts">
                    <TableHead>
                        <TableRow>
                        <TableCell>
                        <SortArrows sortBy={tableParams.sortBy} sortOrder={tableParams.sortOrder} field="serviceLineName" onChange={handleChangeSorting}>
                            <div className="starlink-user-terminal-service-line-toggle">
                                <span className="font-wt-900">SERVICE LINE</span>
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()} >
                                    <Tooltip title="SL Name / SL Number">
                                        <Switch size="small"
                                            checked={switchServiceLineToggle}
                                            onChange={(e) => { e.stopPropagation(); setSwitchServiceLineToggle(!switchServiceLineToggle) }}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />
                                    </Tooltip>
                                </div>
                            </div>
                                </SortArrows>
                            </TableCell>
                            <TableCell>
                                  <span className="font-wt-900">USER</span>
                            </TableCell>
                            <TableCell>  
                                 <span className="font-wt-900">DESCRIPTION</span>
                            </TableCell>
                            <TableCell>
                                   <span className="font-wt-900">RESOURCE</span>
                             </TableCell>
                            <TableCell>
                                   <span className="font-wt-900">ACTION</span>
                            </TableCell>
                            <TableCell>
                                   <span className="font-wt-900">IP ADDRESS</span>
                             </TableCell>
                            <TableCell>
                                <SortArrows sortBy={tableParams.sortBy} sortOrder={tableParams.sortOrder} field="createdAt" onChange={handleChangeSorting}>
                                    <span className="font-wt-900">TIME STAMP</span>
                                </SortArrows>
                                </TableCell>
                            <TableCell>
                                    <span className="font-wt-900">METADATA</span>
                            </TableCell>

                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {auditLogs && auditLogs.length > 0 && auditLogs.map((log:any) => {
                            return <TableRow>
                                 <TableCell>
                                 <Tooltip classes={{ tooltip: 'starlink--tooltip' }} title={
                                                <Grid>
                                                    <Table size="small">
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell classes={{ root: 'starlink--tooltip--value_title' }}>Account Number</TableCell>
                                                                <TableCell>{log.accountNumber ? log.accountNumber : "-"}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell classes={{ root: 'starlink--tooltip--value_title' }}>Service Line Number</TableCell>
                                                                <TableCell>{log.serviceLineNumber ? log.serviceLineNumber : "-"}</TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </Grid>
                                            }>
                                    <span >{switchServiceLineToggle ? log.serviceLineNumber ? log?.serviceLineNumber : "-" : log.serviceLineName ? log?.serviceLineName : "-"}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <span >{log.users ? log.users : "-"}</span>
                                </TableCell>
                                <TableCell>
                                    <span >{log?.userAction ? log?.userAction : ""}</span>
                                </TableCell>
                                <TableCell>
                                    <span >{log?.resource ? log?.resource : "-"}</span>
                                </TableCell>
                                <TableCell>
                                    <span >{log?.userAction ? log?.userAction : "-"}</span>
                                </TableCell>
                                <TableCell>
                                    <span >{log?.IpAddress  ? log?.IpAddress : "-"}</span>
                                </TableCell>
                                <TableCell>
                                    <span >{log?.createdAt ? convertDateTimeIntoTimezone(log?.createdAt, authReducer.userTimezone, MMDDYYYYHMMSS_DATE_FORMAT_24_HRS) : "-"}</span>
                                </TableCell>
                                <TableCell>
                                    <span >
                                         <JsonDataTooltip className="prettyToolTip" data={log.metaData} />
                                    </span>
                                </TableCell>
                            </TableRow>
                        })

                        }
                    </TableBody>
                </Table>
            </TableContainer>

        </Paper>
    );
}

const mapStateToProps = (state) => ({
    authReducer: state.authReducer,
    errorReducer: state.errorReducer,
    dpIds: state.starlinkCloud.dpIds,
    totalAuditLogs: state.starlinkAdmin.totalAuditLogs, 
    auditLogs:state.starlinkAdmin.auditLogs,
    newSummaryServiceLine: state?.authReducer?.newSummaryServiceLine,
    
});

export default withRouter(
    connect(mapStateToProps, {
        getStarlinkAuditLogs,
        clearComponentData
    })(AuditLogs)
);